package com.eladfinish.bible.contest.screens.pairMatching

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.constants.BooleanConstants.ENABLE_PAIR_MATCHING_ALPHABETICAL_ORDER
import com.eladfinish.bible.contest.constants.BooleanConstants.ENABLE_PAIR_MATCHING_LONG_CLICK
import com.eladfinish.bible.contest.constants.BooleanConstants.ENABLE_PAIR_MATCHING_SORTABLE_LIST
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_PAIR_MATCHING_DRAG_ON_LONG_CLICK
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_PAIR_MATCHING_SORTABLE_LIST
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.PAIR_MATCHING_ALPHABETICAL_ORDER
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.data.common.FileStorage
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.extensions.wrapWithParentheses
import com.eladfinish.bible.contest.features.review.ReviewMoment
import com.eladfinish.bible.contest.features.review.ReviewPrompter
import com.eladfinish.bible.contest.network.error.utils.isInternetError
import com.eladfinish.bible.contest.pair_is_not_matched_to
import com.eladfinish.bible.contest.screens.pairMatching.PairMatchingState.DialogState
import com.eladfinish.bible.contest.screens.pairMatching.model.GamePhase
import com.eladfinish.bible.contest.screens.pairMatching.model.KeyItem
import com.eladfinish.bible.contest.screens.pairMatching.model.PairsTopic
import com.eladfinish.bible.contest.screens.pairMatching.model.PairsTopicItem
import com.eladfinish.bible.contest.screens.pairMatching.model.SortableData
import com.eladfinish.bible.contest.screens.pairMatching.model.SpecialTopicItem
import com.eladfinish.bible.contest.screens.pairMatching.model.ValueItem
import com.eladfinish.bible.contest.screens.pairMatching.model.stripAsterisks
import com.eladfinish.bible.contest.screens.pairMatching.usecases.GetPairsUseCase
import com.eladfinish.bible.contest.screens.pairMatching.usecases.SortableUseCase
import com.eladfinish.bible.contest.utils.verbalGrade
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.format
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlin.coroutines.CoroutineContext
import kotlin.time.Duration.Companion.days

class PairMatchingStore(
    private val settings: Settings,
    private val getPairsUseCase: GetPairsUseCase,
    private val sortableUseCase: SortableUseCase,
    private val fileStorage: FileStorage,
    private val reviewPrompter: ReviewPrompter,
    dispatchContext: CoroutineContext = Dispatchers.Default,
) : ViewModelStore<PairMatchingState, PairMatchingAction, PairMatchingEffect>(
    tag = "PairMatchingStore",
    dispatchContext = dispatchContext,
) {

    companion object {
        private const val PREF_LAST_DOWNLOADED_MILLIS = "pairMatchingLastDownloadedMillis"
        private val CACHE_TTL = 7.days.inWholeMilliseconds
    }

    /**
     * Cached topic list — survives dialog dismissal so [handleSelectTopic] can
     * still find the item even after [PairMatchingAction.HideDialog] has already been dispatched.
     */
    private var cachedTopics: List<PairsTopic> = emptyList()

    private val defaultState: PairMatchingState
        get() = PairMatchingState(dialogState = DialogState.None)

    @ExperimentalCoroutinesApi
    override val stateFlow = MutableStateFlow(defaultState)

    // ─── Settings ─────────────────────────────────────────────────────────────

    val isSortableListEnabled: Boolean
        get() = settings.getBoolean(KEY_PAIR_MATCHING_SORTABLE_LIST, ENABLE_PAIR_MATCHING_SORTABLE_LIST)

    val isAlphabeticalOrderEnabled: Boolean
        get() = settings.getBoolean(PAIR_MATCHING_ALPHABETICAL_ORDER, ENABLE_PAIR_MATCHING_ALPHABETICAL_ORDER)

    val isDragOnLongClickEnabled: Boolean
        get() = settings.getBoolean(KEY_PAIR_MATCHING_DRAG_ON_LONG_CLICK, ENABLE_PAIR_MATCHING_LONG_CLICK)

    // ─── Action dispatch ──────────────────────────────────────────────────────

    override fun handleAction(action: PairMatchingAction): PairMatchingState =
        when (action) {
            PairMatchingAction.HideDialog -> handleHideDialog()
            PairMatchingAction.ShowCopyPairsDialog -> handleShowCopyPairsDialog()
            is PairMatchingAction.UpdateTopics -> handleUpdateTopics(action.data)
            PairMatchingAction.LoadTopics -> handleLoadTopics()
            is PairMatchingAction.SelectTopic -> handleSelectTopic(action.index)
            PairMatchingAction.NewGame -> handleNewGame()
            PairMatchingAction.ShowSolution -> handleShowSolution()
            PairMatchingAction.Refresh -> handleRefresh()
            is PairMatchingAction.AttemptMatch -> handleAttemptMatch(action.keyId, action.valueId)
            is PairMatchingAction.SetLoading -> currentState.copy(isLoading = action.isLoading)
            is PairMatchingAction.HandleLoadError -> handleLoadError(action.isNetworkError)
            PairMatchingAction.SortableCompleted -> handleSortableCompleted()
            is PairMatchingAction.RequestCopy -> handleRequestCopy(action.copyAll)
        }

    // ─── Dialog handlers ──────────────────────────────────────────────────────

    private fun handleHideDialog(): PairMatchingState =
        currentState.copy(dialogState = DialogState.None)

    private fun handleShowCopyPairsDialog(): PairMatchingState =
        currentState.copy(dialogState = DialogState.CopyPairs)

    private fun handleUpdateTopics(data: String): PairMatchingState {
        val pairsTopicItems = data.convertToDataClass<List<PairsTopicItem>>().map {
            it.copy(source = it.source.wrapWithParentheses())
        }
        Napier.d("pairsTopicItems: $pairsTopicItems")
        val items = buildList {
            add(SpecialTopicItem(topic = "צור זוגות בעצמך", source = "בפיתוח".wrapWithParentheses()))
            add(SpecialTopicItem(topic = "הצגת זוגות כשאלות אמריקאיות", source = "שאלות מכל הרשימות".wrapWithParentheses()))
            addAll(pairsTopicItems)
        }
        // Cache independently of dialog state — SelectTopic needs this even after HideDialog fires
        cachedTopics = items
        return currentState.copy(
            isLoading = false,
            dialogState = DialogState.TableOfContents(items = items.toImmutableList()),
        )
    }

    // ─── Topic selection ──────────────────────────────────────────────────────

    private fun handleSelectTopic(index: Int): PairMatchingState {
        val items = cachedTopics.takeIf { it.isNotEmpty() } ?: return currentState

        return when (index) {
            0 -> {
                // "Create your own" — not yet implemented
                onlyEffect(PairMatchingEffect.ShowToast("בשלבי פיתוח"))
                handleHideDialog()
            }

            1 -> {
                // Show as multiple-choice questions
                onlyEffect(PairMatchingEffect.NavigateToMultipleChoice)
                handleHideDialog()
            }

            else -> {
                val topic = items.getOrNull(index) as? PairsTopicItem
                    ?: return handleHideDialog()
                if (topic.pairList.isEmpty()) {
                    onlyEffect(PairMatchingEffect.ShowToast("רשימה ריקה!"))
                    return handleHideDialog()
                }
                val newState = handleHideDialog().startGame(topic)
                newState
            }
        }
    }

    private fun PairMatchingState.startGame(topic: PairsTopicItem): PairMatchingState {
        val isSortable = topic.keyTitle == Constants.SERIAL_NUMBER && isSortableListEnabled
        return if (isSortable) {
            val pairs = topic.pairList.associate { it.keyItem to it.valueItem }
            val sortableItems = sortableUseCase.shuffleSortableList(
                pairs = LinkedHashMap(pairs),
                isAlphabeticalOrderEnabled = isAlphabeticalOrderEnabled,
            )
            copy(
                gamePhase = GamePhase.Sortable(
                    topicTitle = topic.topic,
                    columnHeader = topic.valueTitle,
                    items = sortableItems,
                )
            )
        } else {
            val keyItems = topic.pairList.mapIndexed { i, pair ->
                KeyItem(id = i, rawText = pair.keyItem)
            }
            val valueItems = buildValueItems(topic, keyItems)
            copy(
                gamePhase = GamePhase.Playing(
                    topic = topic,
                    keyItems = keyItems,
                    valueItems = valueItems,
                )
            )
        }
    }

    private fun buildValueItems(topic: PairsTopicItem, keyItems: List<KeyItem>): List<ValueItem> {
        val rawValues = topic.pairList.map { it.valueItem }.toMutableList()

        if (isAlphabeticalOrderEnabled) {
            rawValues.sortWith(Comparator { s1, s2 ->
                val n1 = extractInt(s1)
                val n2 = extractInt(s2)
                if (n1 == 0 && n2 == 0) s1.stripAsterisks().compareTo(s2.stripAsterisks())
                else n1 - n2
            })
        } else {
            rawValues.shuffle()
        }

        return rawValues.mapIndexed { i, raw ->
            ValueItem(id = keyItems.size + i, rawText = raw)
        }
    }

    private fun extractInt(s: String): Int {
        if (s.contains("->")) return 0
        return s.replace(Regex("\\D"), "").takeIf { it.isNotEmpty() }?.toIntOrNull() ?: 0
    }

    // ─── Match attempt ────────────────────────────────────────────────────────

    private fun handleAttemptMatch(keyId: Int, valueId: Int): PairMatchingState {
        val playing = currentState.gamePhase as? GamePhase.Playing ?: return currentState

        val keyItem = playing.keyItems.find { it.id == keyId } ?: return currentState
        val valueItem = playing.valueItems.find { it.id == valueId } ?: return currentState

        val expectedValueRaw = playing.topic.pairList
            .find { it.keyItem == keyItem.rawText }
            ?.valueItem

        val newAttempts = playing.numberOfAttempts + 1

        return if (expectedValueRaw == valueItem.rawText) {
            // ── Correct match ────────────────────────────────────────────────
            onlyEffect(PairMatchingEffect.VibrateCorrect(millis = 320))
            val newCorrect = playing.correctPairsSoFar + 1
            val newKeyItems = playing.keyItems.map {
                if (it.id == keyId) it.copy(matchedValueRaw = valueItem.rawText) else it
            }
            val newValueItems = playing.valueItems.map {
                if (it.id == valueId) it.copy(isVisible = false) else it
            }
            val newPlaying = playing.copy(
                keyItems = newKeyItems,
                valueItems = newValueItems,
                numberOfAttempts = newAttempts,
                correctPairsSoFar = newCorrect,
            )
            if (newPlaying.isGameFinished) {
                val grade = newPlaying.grade()
                val msg = "כל הזוגות הותאמו!\nציון: $grade\n${verbalGrade(grade)}"
                onlyEffect(PairMatchingEffect.ShowToast(msg))
                onlyEffect(PairMatchingEffect.PlaySuccessSound)
                onlyEffect(PairMatchingEffect.VibrateCorrect(millis = 500))
                if (!newPlaying.solutionIsShown) {
                    launchEffect {
                        reviewPrompter.onSuccessMoment(moment = ReviewMoment.PAIR_MATCHING, grade = grade)
                    }
                }
            }
            currentState.copy(gamePhase = newPlaying)
        } else {
            // ── Wrong match ──────────────────────────────────────────────────
            onlyEffect(PairMatchingEffect.VibrateWrong(millis = 80))
            val msgDesc = SharedRes.strings.pair_is_not_matched_to
                .format(valueItem.rawText.stripAsterisks(), keyItem.rawText.stripAsterisks())
            onlyEffect(PairMatchingEffect.ShowToastDesc(msgDesc))
            currentState.copy(gamePhase = playing.copy(numberOfAttempts = newAttempts))
        }
    }

    // ─── New game / Solution / Refresh ────────────────────────────────────────

    private fun handleNewGame(): PairMatchingState {
        // Reload topics (cache or server) — shows the loader, then re-opens the ToC dialog.
        launchEffect { loadTopics() }
        return currentState.copy(
            gamePhase = GamePhase.Idle,
            dialogState = DialogState.None,
            isLoading = true,
        )
    }

    private fun handleShowSolution(): PairMatchingState {
        val playing = currentState.gamePhase as? GamePhase.Playing ?: return currentState
        val solvedKeys = playing.keyItems.map { key ->
            if (key.isMatched) key
            else key.copy(
                matchedValueRaw = playing.topic.pairList
                    .find { it.keyItem == key.rawText }?.valueItem
            )
        }
        val solvedValues = playing.valueItems.map { it.copy(isVisible = false) }
        return currentState.copy(
            gamePhase = playing.copy(
                keyItems = solvedKeys,
                valueItems = solvedValues,
                solutionIsShown = true,
            )
        )
    }

    private fun handleRefresh(): PairMatchingState {
        launchEffect { downloadFromServer() }
        return currentState.copy(gamePhase = GamePhase.Idle, isLoading = true)
    }

    // ─── Data loading ─────────────────────────────────────────────────────────

    private fun handleLoadTopics(): PairMatchingState {
        // Only load once — if topics are already loaded (or loading), do nothing.
        if (cachedTopics.isNotEmpty() || currentState.isLoading) return currentState
        launchEffect { loadTopics() }
        return currentState
    }

    /**
     * Serves from cache if it exists and is < 7 days old; otherwise fetches from server.
     */
    private suspend fun loadTopics() {
        // Show the loader for both paths (cache read + server fetch); cleared by UpdateTopics.
        updateState { it.copy(isLoading = true) }

        val fileExists = fileStorage.fileExists(
            folder = Constants.PAIR_MATCHING_FOLDER,
            name = Constants.PAIR_MATCHING_FILENAME,
        )
        val lastDownloadedMillis = settings.getLong(
            key = PREF_LAST_DOWNLOADED_MILLIS,
            defaultValue = 0L,
        )
        val isStillFresh = fileExists &&
                (currentMilliseconds - lastDownloadedMillis) < CACHE_TTL

        Napier.d("loadTopics: fileExists=$fileExists, isStillFresh=$isStillFresh")

        if (isStillFresh) {
            val json = fileStorage.readFile(
                folder = Constants.PAIR_MATCHING_FOLDER,
                name = Constants.PAIR_MATCHING_FILENAME,
            ).orEmpty()
            dispatch(PairMatchingAction.UpdateTopics(data = json))
        } else {
            downloadFromServer()
        }
    }

    private suspend fun downloadFromServer() {
        getPairsUseCase.invoke().fold(
            onSuccess = { json ->
                fileStorage.saveFile(
                    folder = Constants.PAIR_MATCHING_FOLDER,
                    name = Constants.PAIR_MATCHING_FILENAME,
                    content = json,
                )
                settings.putLong(key = PREF_LAST_DOWNLOADED_MILLIS, value = currentMilliseconds)
                updateState { it.copy(isLoading = false) }
                dispatch(PairMatchingAction.UpdateTopics(data = json))
            },
            onFailure = { error ->
                Napier.e("PairMatching download failed", error)
                updateState { it.copy(isLoading = false) }
                val isNetworkError = error.isInternetError()
                dispatch(PairMatchingAction.HandleLoadError(isNetworkError))
            },
        )
    }

    private fun handleRequestCopy(copyAll: Boolean): PairMatchingState {
        val text = buildCopyText(copyAll)
        onlyEffect(PairMatchingEffect.CopyToClipboard(text))
        return currentState
    }

    private fun handleSortableCompleted(): PairMatchingState {
        onlyEffect(PairMatchingEffect.ShowToast("כל הפריטים סודרו! 🎉"))
        onlyEffect(PairMatchingEffect.PlaySuccessSound)
        onlyEffect(PairMatchingEffect.VibrateCorrect(millis = 500))
        return currentState
    }

    private fun handleLoadError(isNetworkError: Boolean): PairMatchingState {
        val msg = if (isNetworkError) "אין חיבור לאינטרנט" else "שגיאה בטעינת הנתונים"
        onlyEffect(PairMatchingEffect.ShowToast(msg))
        return currentState.copy(isLoading = false)
    }

    // ─── Legacy helpers (still used by PairMatchingFragment on V1 path) ───────

    suspend fun getPairs() = getPairsUseCase.invoke()

    fun shuffleSortableList(pairs: LinkedHashMap<String, String>): List<SortableData<String>> =
        sortableUseCase.shuffleSortableList(pairs, isAlphabeticalOrderEnabled)

    // ─── Copy pairs helper ────────────────────────────────────────────────────

    private fun buildCopyText(copyAll: Boolean): String {
        val playing = currentState.gamePhase as? GamePhase.Playing ?: return ""
        val header = "${playing.topic.keyTitle}${StringConstants.DASH}${playing.topic.valueTitle}\n\n"
        val lines = playing.keyItems
            .filter { copyAll || !it.isMatched }
            .joinToString("\n") { key ->
                val prefix = if (key.isMatched) "•" else "?"
                val valueStr = key.matchedValueRaw?.let { StringConstants.DASH + it.stripAsterisks() }.orEmpty()
                "$prefix ${key.rawText.stripAsterisks()}$valueStr"
            }
        return header + lines
    }
}
